import fetch from 'isomorphic-fetch';
import forIn from 'lodash/forIn';
import groupBy from 'lodash/groupBy';

const JSON_HEADERS = new Headers({
  Accept: 'application/json',
  'Content-Type': 'application/json',
});

async function handleResponse(response) {
  if (response.ok) {
    return await response.json();
  } else if (response.status === 401) {
    throw new ApiError(
      response.status,
      'Your session has expired, please login to resume'
    );
  } else if (response.status === 403) {
    const data = await response.json(); 
    if (data.message) {
      throw new ApiError(response.status, data.message); 
    } else {
      throw new ApiError(
        response.status,
        'Your teacher has not set up an activity for this application'
      );
    }
  } else if (response.status === 404) {
    const data = await response.json();
    if (data.message) {
      throw new ApiError(response.status, data.message);
    }
    throw new ApiError(response.status, 'Page not found');
  } else {
    const message = await response.text();
    throw new ApiError(response.status, message || 'A server error occurred');
  }
}

export class ApiError {
  constructor(responseCode, message) {
    this.name = 'ApiError';
    this.responseCode = responseCode;
    this.message = message;
  }
}

class Api {
  constructor(config) {
    this.config = {
      endpoint: '',
      appCode: '',
      token: '',
    };

    if (typeof config === 'object') {
      this.setConfig(config);
    }
  }

  setConfig(config) {
    for (let key in this.config) {
      if (this.config.hasOwnProperty(key) && config.hasOwnProperty(key)) {
        this.config[key] = config[key];
      }
    }
  }

  getHeaders() {
    const headers = JSON_HEADERS;
    headers.set('Authorization', 'Bearer ' + this.config.token);
    return headers;
  }

  whoami() {
    return fetch(`${this.config.endpoint}/student/api/whoami`, {
      headers: {
        Accept: 'application/json',
      },
    }).then(handleResponse);
  }

  login = (studentId, groupId) => {
    let form = new FormData();
    form.append('_studentId', studentId);
    form.append('_groupId', groupId);
    form.append(
      '_target_path',
      `${this.config.endpoint}/student/api/activities`
    );

    return fetch(`${this.config.endpoint}/student/login_check`, {
      method: 'post',
      body: form,
      headers: {
        Accept: 'application/json',
        'X-Request-Token': 'jwt',
      },
    })
      .then(async (response) => {
        const data = await response.clone().json();
        if (response.status === 401) {
          throw new ApiError(response.status, data.error);
        }
        this.config.token = data.token;
        return response;
      })
      .then(handleResponse);
  };

  logout = () => {
    this.config.token = '';
    return fetch(`${this.config.endpoint}/student/logout`, {
      headers: new Headers({ Accept: 'application/json' }),
    }).then((response) => {
      if (response.ok) {
        window.sessionStorage.removeItem('token');
        window.location = "https://whiteboxlearning.com/login"; 
        return {
          message: 'You have been logged out',
        };
      } else {
        throw new ApiError(
          response.code,
          'There was a problem logging out.  Please close your browser for security'
        );
      }
    }).catch(err => {
      console.log('Error logging out', err); 
    });
  };

  getAppList = () => {
    return fetch(`${this.config.endpoint}/student/api/activities`, {
      headers: this.getHeaders(),
      redirect: 'manual',
    }).then(handleResponse);
  };

  start = () => {
    return fetch(
      `${this.config.endpoint}/student/api/startup/${this.config.appCode}`,
      {
        headers: this.getHeaders(),
        redirect: 'manual',
      }
    ).then(handleResponse);
  };

  saveActivity = (timerData, visitData) => {
    return fetch(
      `${this.config.endpoint}/student/api/timeontask/${this.config.appCode}`,
      {
        headers: this.getHeaders(),
        method: 'post',
        body: JSON.stringify({
          times: timerData,
          visits: visitData,
        }),
      }
    ).then(handleResponse);
  };

  saveQuestionResponse = (questionId, answerId) => {
    return fetch(`${this.config.endpoint}/student/api/question/${questionId}`, {
      method: 'PUT',
      body: 'answerId=' + answerId,
      headers: {
        ...this.getHeaders(),
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })
      .then(handleResponse)
      .then((data) => {
        const resp = data.responses.find(
          (item) => item.questionId === questionId
        );
        let {
          id,
          isCorrect,
          isAnswered,
          attempts,
          score,
          correctAnswerExplanation: explanation,
        } = resp;
        let correctAnswerId, correctAnswerText;
        if (resp.correctAnswer) {
          correctAnswerId = resp.correctAnswer.id;
          correctAnswerText = resp.correctAnswer.description;
        }

        return {
          grade: {
            id: data.id,
            exerciseCode: data.exerciseCode,
            score: data.score,
            timeOnTask: data.timeOnTask,
            attempts: data.attempts,
            isComplete: data.isComplete,
            retakesAllowed: data.retakesAllowed,
          },
          response: {
            id,
            questionId,
            answerId: resp.answer.id,
            answerText: resp.answer.description,
            isCorrect,
            isAnswered,
            attempts,
            correctAnswerId,
            correctAnswerText,
            explanation,
            score,
            exerciseCode: data.exerciseCode,
          },
        };
      });
  };

  beginExercise = (code) => {
    return fetch(`${this.config.endpoint}/student/api/quiz/${code}`, {
      method: 'GET',
      headers: this.getHeaders(),
    }).then(handleResponse);
  };

  finishExercise = (code) => {
    return fetch(`${this.config.endpoint}/student/api/quiz/${code}`, {
      method: 'POST',
      headers: this.getHeaders(),
    }).then(handleResponse);
  };

  retakeExercise = (code) => {
    return fetch(
      `${this.config.endpoint}/student/api/grades/${this.config.appCode}/${code}`,
      {
        method: 'PUT',
        body: JSON.stringify({ retake: true }),
        headers: this.getHeaders(),
      }
    ).then(handleResponse);
  };

  markExerciseComplete = (code) => {
    return fetch(
      `${this.config.endpoint}/student/api/grades/${this.config.appCode}/${code}`,
      {
        method: 'PUT',
        body: JSON.stringify({ completed: true }),
        headers: this.getHeaders(),
      }
    ).then(handleResponse);
  };

  getConcepts = () => {
    return fetch(
      `${this.config.endpoint}/student/api/designs/${this.config.appCode}/revisions`,
      {
        headers: this.getHeaders(),
      }
    )
      .then(handleResponse)
      .then((data) => {
        const revisions = data.map((d, i) => {
          d.index = data.length - i;
          return d;
        });

        // get all revisions into an array of concept names and revisions
        let concepts = [];
        forIn(groupBy(revisions, 'name'), function (val, key) {
          concepts.push({
            name: key,
            lastModified: val[0].lastModified,
            specificationId: val[0].specificationId,
            specificationName: val[0].specificationName,
            isShared: val[0]?.isShared,
            revisions: val,
          });
        });

        return {
          revisions,
          concepts,
        };
      });
  };

  forkConcept = (id, name) => {
    return fetch(
      `${this.config.endpoint}/student/api/designs/${this.config.appCode}/fork/${id}`,
      {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ conceptName: name }),
      }
    ).then(handleResponse);
  };

  toggleSharedConcept = (name, shared) => {
    return fetch(
      `${this.config.endpoint}/student/api/designs/${this.config.appCode}/share`,
      {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          conceptName: name,
          shared: shared, // true or false
        }),
      }
    ).then(handleResponse);
  };

  checkDesignName = (name) => {
    const phrase = encodeURIComponent(name);
    return fetch(
      `${this.config.endpoint}/student/api/badword?phrase=${phrase}`,
      {
        headers: this.getHeaders(),
      }
    ).then(handleResponse);
  };

  saveJournalSection = (section, content) => {
    return fetch(
      `${this.config.endpoint}/student/api/journal/${this.config.appCode}/${section}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          content: content,
        }),
        headers: this.getHeaders(),
      }
    ).then(handleResponse);
  };

  saveNotes = (revisionId, notes) => {
    return fetch(
      `${this.config.endpoint}/student/api/notes/${this.config.appCode}/${revisionId}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          notes: notes,
        }),
        headers: this.getHeaders(),
      }
    ).then(handleResponse);
  };

  getContenderList = (params) => {
    return fetch(
      `${this.config.endpoint}/student/api/contenders/${this.config.appCode}?` +
        new URLSearchParams(params),
      {
        method: 'GET',
        headers: this.getHeaders(),
      }
    ).then(handleResponse);
  };

  getActivity = () => {
    return fetch(
      `${this.config.endpoint}/student/api/activities/${this.config.appCode}`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      }
    ).then(handleResponse);
  };

  getGame = () => {
    return fetch(
      `${this.config.endpoint}/student/api/game/${this.config.appCode}`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      }
    ).then(handleResponse);
  };

  levelUp = () => {
    return fetch(
      `${this.config.endpoint}/student/api/game/${this.config.appCode}/levelup`,
      {
        method: 'PUT',
        headers: this.getHeaders(),
      }
    ).then(handleResponse);
  };

  getGameHistory = () => {
    return fetch(
      `${this.config.endpoint}/student/api/game/${this.config.appCode}/history`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      }
    ).then(handleResponse);
  };

  getGameRanking = () => {
    return fetch(
      `${this.config.endpoint}/student/api/game/${this.config.appCode}/game-ranking`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      }
    ).then(handleResponse);
  };

  changeGameLevel = (levelid) => {
    return fetch(
      `${this.config.endpoint}/student/api/game/${this.config.appCode}/selectlevel/${levelid}`,
      {
        method: 'PUT',
        headers: this.getHeaders(),
      }
    ).then(handleResponse);
  };
}

export default Api;
